import { useEffect, useMemo, useState } from "react";
import {
  FiEdit,
  FiPlus,
  FiSave,
  FiShield,
  FiTrash2,
  FiUsers,
  FiX,
  FiSearch,
  FiAlertCircle,
  FiUserPlus,
  FiCheckCircle,
  FiInfo,
} from "react-icons/fi";
import { roleApi } from "../../services/roleApi";
import { userApi } from "../../services/userApi";
import { hasPermission } from "../../utils/permissions";

const emptyPermission = (moduleKey) => ({
  moduleKey,
  canRead: false,
  canCreate: false,
  canUpdate: false,
  canDelete: false,
});

const buildPermissionRows = (modules, permissions = []) => {
  const map = permissions.reduce((acc, p) => {
    acc[p.moduleKey] = p;
    return acc;
  }, {});

  return modules.map((m) => ({
    ...emptyPermission(m.key),
    ...(map[m.key] || {}),
  }));
};

const getLoggedInUser = () => {
  try {
    const saved = localStorage.getItem("user");
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const normalizeRoleName = (value) =>
  String(value || "").trim().toLowerCase();

const isSuperAdminRole = (role) =>
  Boolean(role?.isSystem) &&
  normalizeRoleName(role?.name) === "super admin";

const isSuperAdminManagedUser = (user) =>
  Boolean(
    user?.role === "super_admin" &&
      normalizeRoleName(user?.roleName) === "super admin"
  );

const isSuperAdminUser = () => {
  const user = getLoggedInUser();

  return Boolean(
    user?.role === "super_admin" &&
      normalizeRoleName(user?.roleName) === "super admin" &&
      user?.permissions === "ALL"
  );
};

const isAssignedOnlyRole = (role) => {
  const roleName = String(role?.name || "").toLowerCase();

  return [
    "supplier",
    "partner",
    "forwarder",
    "freight",
    "external",
  ].some((keyword) => roleName.includes(keyword));
};

function Toast({ message, type = "success", onClose }) {
  if (!message) return null;

  const isError = type === "error";

  return (
    <div className="fixed right-5 top-5 z-[70] w-[calc(100%-40px)] max-w-md">
      <div
        className={`flex items-start gap-3 rounded-2xl border bg-white p-4 shadow-2xl dark:bg-slate-900 ${
          isError
            ? "border-red-100 text-red-700 dark:border-red-900 dark:text-red-300"
            : "border-emerald-100 text-emerald-700 dark:border-emerald-900 dark:text-emerald-300"
        }`}
      >
        <div
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
            isError
              ? "bg-red-50 dark:bg-red-950"
              : "bg-emerald-50 dark:bg-emerald-950"
          }`}
        >
          {isError ? <FiAlertCircle /> : <FiCheckCircle />}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">
            {isError ? "Action failed" : "Success"}
          </p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {message}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
        >
          <FiX />
        </button>
      </div>
    </div>
  );
}

function ConfirmDialog({
  open,
  title,
  message,
  confirmText = "Confirm",
  danger = false,
  loading = false,
  onCancel,
  onConfirm,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl border border-slate-100 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div
          className={`grid h-14 w-14 place-items-center rounded-2xl ${
            danger
              ? "bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-300"
              : "bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300"
          }`}
        >
          {danger ? <FiTrash2 className="text-xl" /> : <FiInfo className="text-xl" />}
        </div>

        <h3 className="mt-5 text-xl font-bold text-slate-950 dark:text-white">
          {title}
        </h3>

        <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
          {message}
        </p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            disabled={loading}
            onClick={onCancel}
            className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            className={`rounded-xl px-5 py-3 text-sm font-semibold text-white transition disabled:opacity-60 ${
              danger
                ? "bg-red-600 hover:bg-red-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Processing..." : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 rounded-full transition disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-blue-600" : "bg-slate-300 dark:bg-slate-700"
      }`}
      aria-pressed={checked}
    >
      <span
        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
          checked ? "left-5" : "left-0.5"
        }`}
      />
    </button>
  );
}

function SegmentToggle({ activeTab, setActiveTab, setSearch }) {
  const items = [
    { key: "roles", label: "Roles", icon: FiShield },
    { key: "users", label: "Users", icon: FiUsers },
  ];

  return (
    <div className="inline-flex rounded-2xl border border-slate-200 bg-slate-50 p-1 shadow-sm dark:border-slate-700 dark:bg-slate-950">
      {items.map((item) => {
        const Icon = item.icon;
        const active = activeTab === item.key;

        return (
          <button
            key={item.key}
            type="button"
            onClick={() => {
              setActiveTab(item.key);
              setSearch("");
            }}
            className={`group inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
              active
                ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                : "text-slate-600 hover:bg-white hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
            }`}
          >
            <Icon
              className={`text-base transition ${
                active ? "text-white" : "text-slate-400 group-hover:text-blue-600"
              }`}
            />
            <span>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function InlineMessage({ message, type = "error" }) {
  if (!message) return null;

  const isError = type === "error";

  return (
    <div
      className={`rounded-2xl border px-4 py-3 text-sm ${
        isError
          ? "border-red-100 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300"
          : "border-emerald-100 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
      }`}
    >
      <div className="flex items-start gap-2">
        {isError ? <FiAlertCircle className="mt-0.5" /> : <FiCheckCircle className="mt-0.5" />}
        <span>{message}</span>
      </div>
    </div>
  );
}

function ModalShell({
  title,
  subtitle,
  children,
  footer,
  onClose,
  maxWidth = "max-w-5xl",
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div
        className={`max-h-[92vh] w-full ${maxWidth} overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-slate-900`}
      >
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5 dark:border-slate-800">
          <div>
            <h3 className="text-xl font-bold text-slate-950 dark:text-white">
              {title}
            </h3>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <FiX />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">{children}</div>

        {footer ? (
          <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4 dark:border-slate-800">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function RoleEditor({ modules, role, onClose, onSaved, showToast }) {
  const isEdit = Boolean(role?.id);
  const superAdmin = isSuperAdminUser();
  const protectedSuperAdminRole = isSuperAdminRole(role);
  const canSave =
    (superAdmin || hasPermission("roles", isEdit ? "update" : "create")) &&
    (!protectedSuperAdminRole || superAdmin);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: role?.name || "",
    description: role?.description || "",
    isActive: role?.isActive ?? true,
    permissions: buildPermissionRows(modules, role?.permissions || []),
  });

  const setPermission = (moduleKey, key, value) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.map((p) =>
        p.moduleKey === moduleKey ? { ...p, [key]: value } : p
      ),
    }));
  };

  const setAllForModule = (moduleKey, value) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.map((p) =>
        p.moduleKey === moduleKey
          ? {
              ...p,
              canRead: value,
              canCreate: value,
              canUpdate: value,
              canDelete: value,
            }
          : p
      ),
    }));
  };

  const setAllPermissions = (value) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.map((p) => ({
        ...p,
        canRead: value,
        canCreate: value,
        canUpdate: value,
        canDelete: value,
      })),
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) {
      setError("Role name is required.");
      return;
    }

    if (!canSave) {
      setError("You do not have permission to save roles.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: protectedSuperAdminRole ? role.name : form.name.trim(),
        description: form.description?.trim() || "",
        isActive: protectedSuperAdminRole ? true : Boolean(form.isActive),
        permissions: form.permissions,
      };

      if (isEdit) {
        await roleApi.update(role.id, payload);
      } else {
        await roleApi.create(payload);
      }

      await onSaved();
      showToast(isEdit ? "Role updated successfully." : "Role created successfully.");
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save role.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? "Edit Role" : "Create Role"}
      subtitle="Assign page access and read/create/update/delete actions."
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            type="submit"
            form="role-editor-form"
            disabled={saving || !canSave}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
          >
            <FiSave />
            {saving ? "Saving..." : "Save Role"}
          </button>
        </>
      }
    >
      <form id="role-editor-form" onSubmit={save} className="space-y-5">
        <InlineMessage message={error} type="error" />

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Role name
            </label>
            <input
              value={form.name}
              disabled={protectedSuperAdminRole}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:disabled:bg-slate-800 dark:focus:ring-blue-950"
              placeholder="Example: Accounts Manager"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-700 dark:text-slate-300">
              Description
            </label>
            <input
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              className="mt-2 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950"
              placeholder="Short role description"
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Toggle
              checked={form.isActive}
              disabled={protectedSuperAdminRole}
              onChange={(v) => setForm((prev) => ({ ...prev, isActive: v }))}
            />
            <div>
              <p className="font-semibold text-slate-900 dark:text-slate-100">
                Active role
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Inactive roles cannot be assigned to users.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAllPermissions(true)}
              className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
            >
              Allow All
            </button>

            <button
              type="button"
              onClick={() => setAllPermissions(false)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              Clear All
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800">
          <div className="border-b border-slate-100 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950">
            <h4 className="font-bold text-slate-900 dark:text-slate-100">
              Permission Matrix
            </h4>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Select what this role can do in each module.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-white text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-3">Page</th>
                  <th className="px-4 py-3 text-center">Read</th>
                  <th className="px-4 py-3 text-center">Create</th>
                  <th className="px-4 py-3 text-center">Update</th>
                  <th className="px-4 py-3 text-center">Delete</th>
                  <th className="px-4 py-3 text-center">All</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {modules.map((m) => {
                  const p =
                    form.permissions.find((x) => x.moduleKey === m.key) ||
                    emptyPermission(m.key);

                  const all =
                    p.canRead && p.canCreate && p.canUpdate && p.canDelete;

                  return (
                    <tr
                      key={m.key}
                      className="bg-white transition hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800/70"
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold">{m.label}</p>
                        <p className="text-xs text-slate-400">{m.key}</p>
                      </td>

                      {["canRead", "canCreate", "canUpdate", "canDelete"].map(
                        (key) => (
                          <td key={key} className="px-4 py-3 text-center">
                            <Toggle
                              checked={Boolean(p[key])}
                              onChange={(v) => setPermission(m.key, key, v)}
                            />
                          </td>
                        )
                      )}

                      <td className="px-4 py-3 text-center">
                        <Toggle
                          checked={all}
                          onChange={(v) => setAllForModule(m.key, v)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

function UserEditor({ roles, user, onClose, onSaved, showToast }) {
  const isEdit = Boolean(user?.id);
  const superAdmin = isSuperAdminUser();
  const loggedInUser = getLoggedInUser();
  const editingSelf =
    isEdit && Number(user?.id) === Number(loggedInUser?.id);
  const editingProtectedSuperAdmin =
    isEdit && isSuperAdminManagedUser(user);
  const canSave =
    (superAdmin || hasPermission("users", isEdit ? "update" : "create")) &&
    (!editingProtectedSuperAdmin || superAdmin);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const initialRoleId = user?.roleId || roles[0]?.id || "";
  const initialRole = roles.find(
    (role) => String(role.id) === String(initialRoleId)
  );

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    password: "",
    roleId: initialRoleId,
    accessScope:
      isSuperAdminRole(initialRole)
        ? "all"
        : user?.accessScope ||
          (isAssignedOnlyRole(initialRole) ? "assigned" : "all"),
    isActive: user?.isActive ?? true,
  });

  const selectedRole = roles.find(
    (role) => String(role.id) === String(form.roleId)
  );
  const selectedRoleIsSuperAdmin = isSuperAdminRole(selectedRole);

  const save = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }

    if (!form.email.trim()) {
      setError("Email is required.");
      return;
    }

    if (!isEdit && !form.password) {
      setError("Password is required for new user.");
      return;
    }

    if (!form.roleId) {
      setError("Role is required.");
      return;
    }

    if (!canSave) {
      setError("You do not have permission to save users.");
      return;
    }

    if (selectedRoleIsSuperAdmin && !superAdmin) {
      setError("Only a Super Admin can assign the Super Admin role.");
      return;
    }

    if (editingSelf && form.isActive === false) {
      setError("You cannot deactivate your own account.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
        roleId: form.roleId,
        accessScope: selectedRoleIsSuperAdmin ? "all" : form.accessScope,
        isActive: Boolean(form.isActive),
      };

      if (isEdit && !payload.password) {
        delete payload.password;
      }

      if (isEdit) {
        await userApi.update(user.id, payload);
      } else {
        await userApi.create(payload);
      }

      await onSaved();
      showToast(isEdit ? "User updated successfully." : "User created successfully.");
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save user.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={isEdit ? "Edit User" : "Create User"}
      subtitle="Assign the correct role and account status to this user."
      onClose={onClose}
      maxWidth="max-w-2xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            type="submit"
            form="user-editor-form"
            disabled={saving || !canSave}
            className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save User"}
          </button>
        </>
      }
    >
      <form id="user-editor-form" onSubmit={save} className="space-y-5">
        <InlineMessage message={error} type="error" />

        <div className="grid gap-4 md:grid-cols-2">
          <input
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950"
            placeholder="Name"
            value={form.name}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, name: e.target.value }))
            }
          />

          <input
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950"
            placeholder="Email"
            value={form.email}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, email: e.target.value }))
            }
          />

          <input
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950"
            placeholder={isEdit ? "New password optional" : "Password"}
            type="password"
            value={form.password}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, password: e.target.value }))
            }
          />

          <select
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:disabled:bg-slate-800 dark:focus:ring-blue-950"
            value={form.roleId}
            disabled={editingProtectedSuperAdmin && !superAdmin}
            onChange={(event) => {
              const selectedRoleId = event.target.value;
              const selectedRole = roles.find(
                (role) => String(role.id) === String(selectedRoleId)
              );

              setForm((previous) => ({
                ...previous,
                roleId: selectedRoleId,
                accessScope: isSuperAdminRole(selectedRole)
                  ? "all"
                  : isAssignedOnlyRole(selectedRole)
                    ? "assigned"
                    : "all",
              }));
            }}
          >
            <option value="">Select role</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>

          <input
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950"
            placeholder="Phone number (+44...)"
            type="tel"
            value={form.phone}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                phone: event.target.value,
              }))
            }
          />

          <select
            className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-50 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:disabled:bg-slate-800 dark:focus:ring-blue-950"
            value={selectedRoleIsSuperAdmin ? "all" : form.accessScope}
            disabled={selectedRoleIsSuperAdmin}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                accessScope: event.target.value,
              }))
            }
          >
            <option value="all">All Containers — Internal User</option>
            <option value="assigned">
              Assigned Containers Only — Supplier / Forwarder
            </option>
          </select>
        </div>

        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
          <Toggle
            checked={form.isActive}
            disabled={
              editingSelf || (editingProtectedSuperAdmin && !superAdmin)
            }
            onChange={(v) => setForm((prev) => ({ ...prev, isActive: v }))}
          />
          <div>
            <p className="font-semibold text-slate-900 dark:text-slate-100">
              Active user
            </p>
            <p className="text-sm text-slate-500">Disabled users cannot login.</p>
          </div>
        </div>
      </form>
    </ModalShell>
  );
}

export default function RoleManagement() {
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [roleEditor, setRoleEditor] = useState(null);
  const [userEditor, setUserEditor] = useState(null);
  const [activeTab, setActiveTab] = useState("roles");
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [confirm, setConfirm] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const loggedInUser = getLoggedInUser();
  const superAdmin = isSuperAdminUser();

  const canCreateRole = superAdmin || hasPermission("roles", "create");
  const canUpdateRole = superAdmin || hasPermission("roles", "update");
  const canDeleteRole = superAdmin || hasPermission("roles", "delete");

  const canCreateUser = superAdmin || hasPermission("users", "create");
  const canUpdateUser = superAdmin || hasPermission("users", "update");
  const canDeleteUser = superAdmin || hasPermission("users", "delete");

  const showToast = (message, type = "success") => {
    setToast({ message, type });

    setTimeout(() => {
      setToast({ message: "", type: "success" });
    }, 2500);
  };

  const loadData = async () => {
    setLoading(true);

    try {
      const [rolesRes, usersRes] = await Promise.all([
        roleApi.list(),
        userApi.list(),
      ]);

      setRoles(rolesRes.data?.roles || []);
      setModules(rolesRes.data?.modules || []);
      setUsers(usersRes.data?.users || []);
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to load roles/users.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const roleOptions = useMemo(
    () =>
      roles.filter(
        (role) =>
          role.isActive &&
          (superAdmin || !isSuperAdminRole(role))
      ),
    [roles, superAdmin]
  );

  const filteredRoles = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return roles;

    return roles.filter((role) =>
      [role.name, role.description].join(" ").toLowerCase().includes(q)
    );
  }, [roles, search]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return users;

    return users.filter((user) =>
      [user.name, user.email, user.roleName]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [users, search]);

  const requestDeleteRole = (role) => {
    if (!canDeleteRole) {
      showToast("You do not have permission to delete roles.", "error");
      return;
    }

    if (role.isSystem) {
      showToast("System roles cannot be deleted.", "error");
      return;
    }

    const assignedUsers = Number(role.usersCount || 0);

    setConfirm({
      type: "role",
      item: role,
      title: "Delete role?",
      message:
        assignedUsers > 0
          ? `"${role.name}" is assigned to ${assignedUsers} user(s). The system may stop deletion until users are moved to another role.`
          : `Are you sure you want to delete "${role.name}"? This action cannot be undone.`,
      confirmText: "Delete Role",
      danger: true,
    });
  };

  const requestDeleteUser = (user) => {
    if (!canDeleteUser) {
      showToast("You do not have permission to delete users.", "error");
      return;
    }

    if (Number(user.id) === Number(loggedInUser?.id)) {
      showToast("You cannot delete your own account.", "error");
      return;
    }

    if (isSuperAdminManagedUser(user) && !superAdmin) {
      showToast(
        "Only a Super Admin can delete another Super Admin.",
        "error"
      );
      return;
    }

    setConfirm({
      type: "user",
      item: user,
      title: "Delete user?",
      message: `Are you sure you want to delete "${user.name}"? This action cannot be undone.`,
      confirmText: "Delete User",
      danger: true,
    });
  };

  const handleConfirm = async () => {
    if (!confirm) return;

    setConfirmLoading(true);

    try {
      if (confirm.type === "role") {
        await roleApi.remove(confirm.item.id);
        showToast("Role deleted successfully.");
      }

      if (confirm.type === "user") {
        await userApi.remove(confirm.item.id);
        showToast("User deleted successfully.");
      }

      setConfirm(null);
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.message || "Delete failed.", "error");
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <>
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "success" })}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        danger={confirm?.danger}
        loading={confirmLoading}
        onCancel={() => setConfirm(null)}
        onConfirm={handleConfirm}
      />

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="border-b border-slate-100 px-6 py-5 dark:border-slate-800">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
                  <FiShield />
                </div>

                <div>
                  <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                    Role & Access Management
                  </h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Create roles, assign users, and control read/create/update/delete access.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row lg:items-center">
              <SegmentToggle
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                setSearch={setSearch}
              />

              {activeTab === "roles" ? (
                <button
                  type="button"
                  disabled={!canCreateRole}
                  onClick={() => setRoleEditor({})}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                >
                  <FiPlus />
                  Add Role
                </button>
              ) : (
                <button
                  type="button"
                  disabled={!canCreateUser}
                  onClick={() => setUserEditor({})}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                >
                  <FiUserPlus />
                  Add User
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="border-b border-slate-100 px-6 py-4 dark:border-slate-800">
          <div className="relative max-w-md">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                activeTab === "roles" ? "Search roles..." : "Search users..."
              }
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-50 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-blue-950"
            />
          </div>
        </div>

        {loading ? (
          <div className="py-14 text-center text-sm text-slate-500">
            Loading access management...
          </div>
        ) : activeTab === "roles" ? (
          <div className="p-6">
            <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-4">Role</th>
                      <th className="px-5 py-4">Users</th>
                      <th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4 text-right">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredRoles.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-5 py-10 text-center text-slate-500"
                        >
                          No roles found.
                        </td>
                      </tr>
                    ) : (
                      filteredRoles.map((role) => (
                        <tr
                          key={role.id}
                          className="bg-white transition hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800/70"
                        >
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="grid h-10 w-10 place-items-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
                                <FiShield />
                              </div>

                              <div>
                                <p className="font-bold text-slate-950 dark:text-white">
                                  {role.name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {role.description || "-"}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-5 py-4">
                            <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                              <FiUsers />
                              {role.usersCount || 0}
                            </span>
                          </td>

                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                                role.isActive
                                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                              }`}
                            >
                              <span
                                className={`h-2 w-2 rounded-full ${
                                  role.isActive ? "bg-emerald-500" : "bg-slate-400"
                                }`}
                              />
                              {role.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                disabled={
                                  !canUpdateRole ||
                                  (isSuperAdminRole(role) && !superAdmin)
                                }
                                onClick={() => setRoleEditor(role)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-blue-950"
                                title="Edit role"
                              >
                                <FiEdit />
                              </button>

                              <button
                                type="button"
                                disabled={!canDeleteRole || role.isSystem}
                                onClick={() => requestDeleteRole(role)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-100 text-red-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                                title="Delete role"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-3 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300">
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <p>
                System roles cannot be deleted. Only a Super Admin can manage
                the protected Super Admin role and Super Admin users.
              </p>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                    <tr>
                      <th className="px-5 py-4">User</th>
                      <th className="px-5 py-4">Role</th>
                      <th className="px-5 py-4">Status</th>
                      <th className="px-5 py-4 text-right">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-5 py-10 text-center text-slate-500"
                        >
                          No users found.
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => (
                        <tr
                          key={user.id}
                          className="bg-white transition hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800/70"
                        >
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-sm font-bold text-white">
                                {(user.name || "U").slice(0, 1).toUpperCase()}
                              </div>

                              <div>
                                <p className="font-bold text-slate-950 dark:text-white">
                                  {user.name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </td>

                          <td className="px-5 py-4">
                            <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                              {user.roleName || "-"}
                            </span>
                          </td>

                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                                user.isActive
                                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                                  : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                              }`}
                            >
                              <span
                                className={`h-2 w-2 rounded-full ${
                                  user.isActive ? "bg-emerald-500" : "bg-slate-400"
                                }`}
                              />
                              {user.isActive ? "Active" : "Disabled"}
                            </span>
                          </td>

                          <td className="px-5 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                disabled={
                                  !canUpdateUser ||
                                  (isSuperAdminManagedUser(user) && !superAdmin)
                                }
                                onClick={() => setUserEditor(user)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-blue-950"
                                title="Edit user"
                              >
                                <FiEdit />
                              </button>

                              <button
                                type="button"
                                disabled={
                                  !canDeleteUser ||
                                  Number(user.id) === Number(loggedInUser?.id) ||
                                  (isSuperAdminManagedUser(user) && !superAdmin)
                                }
                                onClick={() => requestDeleteUser(user)}
                                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-100 text-red-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                                title="Delete user"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {roleEditor ? (
          <RoleEditor
            modules={modules}
            role={roleEditor.id ? roleEditor : null}
            onClose={() => setRoleEditor(null)}
            onSaved={loadData}
            showToast={showToast}
          />
        ) : null}

        {userEditor ? (
          <UserEditor
            roles={roleOptions}
            user={userEditor.id ? userEditor : null}
            onClose={() => setUserEditor(null)}
            onSaved={loadData}
            showToast={showToast}
          />
        ) : null}
      </div>
    </>
  );
}